// A design system confirmation, replacing window.confirm.
//
// One provider near the root, one hook everywhere else:
//
//   const confirm = useConfirm()
//   if (!(await confirm(`Remove ${name}?`))) return
//
// Rendered through the Dialog primitive, which traps focus, closes on the
// scrim and on Escape, and returns focus where it was. Cancel sits first in
// the footer so it takes initial focus: the confirming button is
// destructive, and Enter out of habit should not remove anything.

import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { Button, Dialog } from './components/ui/index.js'

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const resolveRef = useRef(null)
  const [request, setRequest] = useState(null)

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve
      setRequest({
        message,
        title: options.title ?? 'Are you sure?',
        confirmLabel: options.confirmLabel ?? 'Remove',
      })
    })
  }, [])

  function settle(answer) {
    resolveRef.current?.(answer)
    resolveRef.current = null
    setRequest(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request ? (
        <Dialog
          title={request.title}
          onClose={() => settle(false)}
          footer={
            <>
              <Button onClick={() => settle(false)}>Cancel</Button>
              <Button variant="primary" tone="bad" onClick={() => settle(true)}>
                {request.confirmLabel}
              </Button>
            </>
          }
        >
          <p>{request.message}</p>
        </Dialog>
      ) : null}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext)
  if (!confirm) {
    throw new Error('useConfirm must be used inside ConfirmProvider')
  }
  return confirm
}
